import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Team } from 'modules/teams/domain/interfaces/team';
import { useTeamList } from 'modules/teams/ui/hooks/use-teams-list';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TEAM_DEFAULT_LOGO } from 'shared/constants/links';
import { UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import Modal from 'ud-ui/components/modal';
import { UDEntityRow, UDPagedList } from 'ud-ui/components/paged-list';

type Source = 'common' | 'players';

const SEARCH_PLACEHOLDER = 'Поиск по командам';
const CREATE_BUTTON_TEXT = 'Добавить команду';

const DEFAULT_DELETE_ERROR = 'Не удалось удалить команду';

const getDeleteErrorMessage = (error: unknown): string => {
  const message = typeof error === 'string' ? error : error instanceof Error ? error.message : '';

  if (message.includes('Cannot delete team with matches')) {
    return 'Невозможно удалить команду: у неё есть сыгранные матчи';
  }

  return DEFAULT_DELETE_ERROR;
};

const TeamsPage = () => {
  const navigate = useNavigate();
  const handleCreateClick = useCallback(() => {
    navigate(Pages.TEAMS.CREATE);
  }, [navigate]);
  const { setHeaderContent } = useMainLayout();

  const {
    items,
    itemsOffset,
    state,
    page,
    totalPages,
    perPage,
    draftSearch,
    setPage,
    setPerPage,
    setSearch,
    buildPageHref,
    deleteTeam,
  } = useTeamList();

  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const openDelete = useCallback((team: Team) => {
    setTeamToDelete(team);
  }, []);
  const closeDelete = useCallback(() => {
    setTeamToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!teamToDelete) return;
    setIsDeleting(true);
    try {
      await deleteTeam(teamToDelete.id);
      setTeamToDelete(null);
      toast.success('Команда успешно удалена');
    } catch (error) {
      toast.error(getDeleteErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTeam, teamToDelete]);

  const handleEditInfoClick = useCallback(
    (team: { id: number }, source: Source) => {
      navigate(Pages.TEAMS.builders.edit(team.id, source));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title="Команды" createButtonText={CREATE_BUTTON_TEXT} createButtonOnClick={handleCreateClick} />,
    );
    return () => setHeaderContent(null);
  }, [handleCreateClick, setHeaderContent]);

  // Номер строки продолжается со страницы на страницу: на второй странице при
  // десяти элементах отсчёт идёт с 11-го, а не снова с первого. Считается от
  // `itemsOffset`, а не от `page`: пока едет следующая страница, на экране ещё
  // прошлые элементы, и перенумеровывать их раньше времени нельзя.
  const renderTeam = useCallback(
    (team: Team, index: number) => (
      <UDEntityRow
        index={itemsOffset + index + 1}
        avatar={team.logo?.url ?? TEAM_DEFAULT_LOGO}
        title={`${team.name}${team.shortName ? ` (${team.shortName})` : ''}`}
        subtitle={
          <span className="flex gap-4">
            <span>Дата создания: {team.createdAt ?? '-'}</span>
            <span>Состав: {team.players.length}</span>
          </span>
        }
        actions={
          <UDButton variant="secondary" style={{ width: 144 }} onClick={() => openDelete(team)}>
            Удалить
          </UDButton>
        }
        onClick={() => handleEditInfoClick(team, 'common')}
      />
    ),
    [handleEditInfoClick, openDelete, itemsOffset],
  );

  return (
    <>
      <UDPagedList<Team>
        items={items}
        renderItem={renderTeam}
        getItemKey={(team) => team.id}
        state={state}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
        pageHref={buildPageHref}
        search={draftSearch}
        onSearchChange={setSearch}
        searchPlaceholder={SEARCH_PLACEHOLDER}
        emptyText="Команд пока нет"
        emptyAction={
          <UDButton variant="primary" onClick={handleCreateClick}>
            {CREATE_BUTTON_TEXT}
          </UDButton>
        }
        notFoundText="Команды не найдены"
      />

      <Modal show={Boolean(teamToDelete)} onClose={closeDelete}>
        <Modal.Body className={'min-h-10 min-w-100 text-center'}>
          <UDText as="h2" className="mt-2.5" variant="title-3">
            Удалить команду «{teamToDelete?.name}»?
          </UDText>
          <UDText variant="text-2">Это действие необратимо.</UDText>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex w-full gap-3">
            <UDButton variant="secondary" onClick={closeDelete} disabled={isDeleting} stretch>
              Отмена
            </UDButton>
            <UDButton variant="primary" onClick={handleConfirmDelete} loading={isDeleting} stretch>
              Удалить
            </UDButton>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TeamsPage;
